import { Ionicons } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { Link, router, useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, ScrollView } from "react-native";
import { useDispatch } from "react-redux";

import Loader from "@/components/Loader";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { ProfileRequest } from "@/utils/dto/accounts.request";
import { CounterPartyIntermediaryIntermediaryType } from "@/utils/dto/extracted_enums";

type FormData = {
  intermediaryType: string;
  institutionName: string;
  swiftBic: string;
};

export default function Intermediary() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentTransactionId, setCurrentTransactionId] = useState("");
  const [screenFocused, setScreenFocused] = useState(false);
  const globalValues = useAppSelector((state: any) => state);

  const resolver = classValidatorResolver(ProfileRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState, reset } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const { isSubmitting } = formState;

  useFocusEffect(
    React.useCallback(() => {
      setScreenFocused(true);
      if (
        currentTransactionId !== globalValues.currentTransactionId ||
        globalValues.currentTransactionId === ""
      ) {
        reset();
        setCurrentTransactionId(globalValues.currentTransactionId);
      }
      return () => {
        setScreenFocused(false);
      };
    }, [globalValues.currentTransactionId, screenFocused])
  );

  const onSubmit = async (data: FormData) => {
    const profileInformationData =
      globalValues.profileInformationData !== null
        ? typeof globalValues.profileInformationData === "string"
          ? JSON.parse(globalValues.profileInformationData)
          : globalValues.profileInformationData
        : null;

    const profileAddressData =
      globalValues.profileAddressData !== null
        ? typeof globalValues.profileAddressData === "string"
          ? JSON.parse(globalValues.profileAddressData)
          : globalValues.profileAddressData
        : null;

    const accountInformationData =
      globalValues.accountInformationData !== null
        ? typeof globalValues.accountInformationData === "string"
          ? JSON.parse(globalValues.accountInformationData)
          : globalValues.accountInformationData
        : null;

    const accountAddressData =
      globalValues.accountAddressData !== null
        ? typeof globalValues.accountAddressData === "string"
          ? JSON.parse(globalValues.accountAddressData)
          : globalValues.accountAddressData
        : null;

    const requestData = {
      counterpartyType: globalValues.counterpartyType,
      rails: [globalValues.rail],
      profile: {
        ...profileInformationData,
        address: {
          ...profileAddressData,
        },
      },
      accountInformation: {
        ...accountInformationData,
        institutionAddress: {
          ...accountAddressData,
        },
      },
      intermediaryInformation: {
        ...(data || {}),
      },
    };
    try {
      const request = await sendRequest(
        "post",
        "/counterparty",
        requestData,
        true
      );
      if (request) {
        dispatch(setForm({ counterpartyId: request.id }));
        router.push("home/transaction/counterparty");
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1 p-4 lg:p-0">
      <Link href="/home/transaction/accountaddress" asChild>
        <View className="absolute top-0 left-0 m-1.5 z-50">
          <Ionicons name="chevron-back-outline" size={28} color="#BDBDBD" />
        </View>
      </Link>
      <Link href="/home" asChild>
        <View className="absolute top-0 right-0 m-1 z-50">
          <Ionicons name="close-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <View className="m-2">
        <Title text={t("home/intermediary:title")} />
      </View>
      <View className="z-50 flex-grow w-full items-center justify-center">
        <View className="z-40">
          <InputChoose
            label={t("home/intermediary:input.intermediarytype")}
            control={control}
            name="intermediaryType"
            data={{ CounterPartyIntermediaryIntermediaryType }}
          />
        </View>
        <Input
          label={t("home/intermediary:input.institutionname")}
          control={control}
          name="institutionName"
        />
        <Input
          label={t("home/intermediary:input.swiftbic")}
          control={control}
          name="swiftBic"
        />
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
      />
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
